import { useMemo, useCallback } from 'react';
import PortalIdParser from 'PortalIdParser';
import { useHistory } from 'react-router-dom';
import emptyFunction from 'react-utils/emptyFunction';
import { CommentObjectType, LayoutWithCollaborationSidebar, FEATURES, SUPPORTED_HUBS } from 'collaboration-sidebar';
import { ACTION_MODULE_ID_PREFIX, getCommentableAreaId } from 'automation-ui-management/constants/Commenting';

// We use a regex that matches nothing for the details page as there are no commentable areas in that app.
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const NEVER_MATCHING_REGEX = /a^/;
const CANVAS_ROUTES_REGEX = /\/workflows\/[0-9]+\/platform\/flow\/[0-9]+\/(edit|settings\/(general|suppressions)|goals|changes|)/;
const CollaborationSidebar = ({
  children,
  flowId,
  isDetailsPage = false,
  scrollablePage = true,
  withoutSidebar,
  ctaUrl,
  dragAndDropAreas,
  defaultSidebarPlacement,
  appName
}) => {
  const history = useHistory();

  // This function navigates the app to a given path and makes
  // the commentable area for a given moduleId available in
  // the DOM so we can highlight and scroll to it on interaction
  // of a collaboration sidebar comment indicator.
  const navigateTo = useCallback((path, moduleId) => {
    // Navigate to the same path the comment exists on
    if (path !== window.location.pathname) {
      const trimmedPath = path.replace(`/workflows/${PortalIdParser.get()}`, '');
      history.push(trimmedPath);
    }

    // If an action commentable area isn't in the DOM,
    // we'll use the scrollToAction handler to scroll to there first.
    if (moduleId.startsWith(ACTION_MODULE_ID_PREFIX)) {
      const commentableArea = document.querySelector(getCommentableAreaId(moduleId));
      const actionId = parseInt(moduleId.replace(ACTION_MODULE_ID_PREFIX, ''), 10);
      history.replace({
        state: {
          scrollToAction: {
            actionId: !commentableArea ? actionId : undefined,
            scrollBehavior: 'auto',
            retry: !commentableArea ? true : undefined
          }
        },
        search: history.location.search
      });
    }
  }, [history]);
  const appSupportedRoutesRegex = useMemo(() => isDetailsPage ? NEVER_MATCHING_REGEX : CANVAS_ROUTES_REGEX, [isDetailsPage]);
  const embeddedCommentsProps = useMemo(() => ({
    appSupportedRoutesRegex,
    indicatorNavigateTo: isDetailsPage ? emptyFunction : navigateTo
  }), [appSupportedRoutesRegex, isDetailsPage, navigateTo]);
  const objectId = flowId.toString();
  const defaultAppName = 'workflows';
  const appNameForCollaborationSidebar = appName || defaultAppName;
  return /*#__PURE__*/_jsx(LayoutWithCollaborationSidebar, {
    appName: appNameForCollaborationSidebar
    /*
    appHub specifies which hubs this app is a part of to let the sidebar check the correct scope for access as it is available per hub.
    Since workflows are a special case that are enabled with any pro hub, we are currently passing in all the hubs we support.
    */,
    appHub: Object.values(SUPPORTED_HUBS),
    embeddedCommentsProps: embeddedCommentsProps,
    enabledFeatures: [FEATURES.COMMENTS, FEATURES.DEEP_COMMENTING],
    isOverlay: true,
    objectId: objectId,
    objectType: CommentObjectType.WORKFLOW,
    scrollablePage: scrollablePage,
    withoutSidebar: withoutSidebar,
    ctaUrl: ctaUrl,
    dragAndDropAreas: dragAndDropAreas,
    defaultSidebarPlacement: defaultSidebarPlacement,
    children: /*#__PURE__*/_jsx(_Fragment, {
      children: children
    })
  });
};
export default CollaborationSidebar;